// Pure helpers behind the proposal Edit form (standing-agent-ui "Per-proposal
// decisions"): which fields a tool's input schema describes, inline validation
// against that schema, and the before/after rows for changed fields. The server
// validates edited arguments again (ToolArgumentValidator) — this is for fast,
// readable feedback, never the security boundary.

import type { JsonSchema } from '@shared/api-types';

export type FieldKind = 'string' | 'enum' | 'number' | 'integer' | 'boolean' | 'json';

export interface SchemaField {
  name: string;
  kind: FieldKind;
  required: boolean;
  schema: JsonSchema;
  label: string;
}

function primaryType(schema: JsonSchema): string | undefined {
  if (Array.isArray(schema.type)) return schema.type.find((t) => t !== 'null');
  return schema.type;
}

function kindOf(schema: JsonSchema): FieldKind {
  if (schema.enum && schema.enum.length > 0) return 'enum';
  switch (primaryType(schema)) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'integer':
      return 'integer';
    case 'boolean':
      return 'boolean';
    default:
      return 'json';
  }
}

/**
 * The editable fields: every schema property (in schema order), then any
 * argument the agent sent that the schema doesn't name (edited as JSON).
 */
export function schemaFields(schema: JsonSchema | null | undefined, args: Record<string, unknown>): SchemaField[] {
  const props = schema?.properties ?? {};
  const required = new Set(schema?.required ?? []);
  const fields: SchemaField[] = Object.entries(props).map(([name, s]) => ({
    name,
    kind: kindOf(s),
    required: required.has(name),
    schema: s,
    label: s.title || name,
  }));
  for (const name of Object.keys(args)) {
    if (!(name in props)) fields.push({ name, kind: 'json', required: false, schema: {}, label: name });
  }
  return fields;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '';
}

/** Validate one field's value; returns a message or null. */
export function validateField(field: SchemaField, value: unknown): string | null {
  const s = field.schema;
  if (isEmpty(value)) return field.required ? `${field.label} is required.` : null;
  switch (field.kind) {
    case 'string': {
      if (typeof value !== 'string') return `${field.label} must be text.`;
      if (s.minLength != null && value.length < s.minLength) return `${field.label} needs at least ${s.minLength} characters.`;
      if (s.maxLength != null && value.length > s.maxLength) return `${field.label} can have at most ${s.maxLength} characters.`;
      if (s.pattern) {
        try {
          if (!new RegExp(s.pattern).test(value)) return `${field.label} isn't in the expected format.`;
        } catch {
          // An unparseable pattern is the server's problem to report.
        }
      }
      return null;
    }
    case 'enum':
      return s.enum!.some((e) => e === value) ? null : `${field.label} must be one of the listed values.`;
    case 'number':
    case 'integer': {
      if (typeof value !== 'number' || Number.isNaN(value)) return `${field.label} must be a number.`;
      if (field.kind === 'integer' && !Number.isInteger(value)) return `${field.label} must be a whole number.`;
      if (s.minimum != null && value < s.minimum) return `${field.label} must be at least ${s.minimum}.`;
      if (s.maximum != null && value > s.maximum) return `${field.label} must be at most ${s.maximum}.`;
      return null;
    }
    case 'boolean':
      return typeof value === 'boolean' ? null : `${field.label} must be yes or no.`;
    case 'json':
      return null;
  }
}

/** Validate the whole argument object; keyed by field name. */
export function validateArgs(fields: SchemaField[], args: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const msg = validateField(f, args[f.name]);
    if (msg) out[f.name] = msg;
  }
  return out;
}

function canonical(v: unknown): string {
  if (v === undefined) return 'undefined';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
    .join(',')}}`;
}

export interface ArgChange {
  name: string;
  before: unknown;
  after: unknown;
}

/** Fields whose value differs between `before` and `after` (structural compare). */
export function diffArgs(before: Record<string, unknown>, after: Record<string, unknown>): ArgChange[] {
  const names = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  return names
    .filter((n) => canonical(before[n]) !== canonical(after[n]))
    .map((n) => ({ name: n, before: before[n], after: after[n] }));
}

/** A value as one readable line for the diff table. */
export function displayValue(v: unknown): string {
  if (v === undefined) return '(not set)';
  if (v === null) return 'empty';
  if (typeof v === 'string') return v === '' ? '""' : v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Drop undefined / empty-string optional values before sending. */
export function cleanArgs(fields: SchemaField[], args: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(args)) {
    const f = fields.find((x) => x.name === k);
    if (v === undefined) continue;
    if (v === '' && f && !f.required) continue;
    out[k] = v;
  }
  return out;
}
